import React, { useState, useMemo } from "react";
import { Box, Button, Typography } from "@mui/material";
import { mainOrderStatus } from "../../constants/orderStatus";
import { orderObjectStatus } from "../../constants/orderStatus";
import { PicturesModal } from "./picturesModal";
import { NumberInput } from "../../components/numberInput";
import Photos from '../../components/serviceOrders/photos';
import Modal from '@mui/material/Modal';
import supabase from '../../supabase';
import { FullScreenLoader } from '../../components/spinner/FullScreenLoader';
import imageCompression from "browser-image-compression";
import { v4 as uuidv4 } from "uuid";
import { ErrorModal } from "../../components/ModalAlert";
import { currencyValues } from "../../constants/paymentValues";
import { FormCard, Field, TextInput, SelectInput } from "../../components/ui/form";
import { Num, OrderStatusChip } from "../../components/ui";
import { IconEye, IconCamera, IconPen } from "../../components/ui/icons";
import { tokens } from "../../theme";

/** Dato de sólo lectura del encabezado: etiqueta chica arriba, valor abajo. */
const Dato = ({ label, children, numeric }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
    <Typography sx={{ fontSize: "11.5px", fontWeight: 500, color: tokens.ink3 }}>{label}</Typography>
    {numeric ? (
      <Num sx={{ fontSize: "13.5px", fontWeight: 600 }}>{children}</Num>
    ) : (
      <Typography sx={{ fontSize: "13.5px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>
        {children}
      </Typography>
    )}
  </Box>
);

const HeadOrder = ({
  order,
  setOrder,
  disableEdition,
  orderDetails = [],
  setOrderDetails,
}) => {
  const [openPhotoModal, setOpenPhotoModal] = useState(false);
  const [photosUrlBakground, setPhotosUrlBakground] = useState([]);
  const [openEditPhotos, setOpenEditPhotos] = useState(false);
  const [isClosingModal, setIsClosingModal] = useState(false);
  // Estados locales para edición de fotos
  const [formStateLocal, setFormStateLocal] = useState({ pictures: order.files || [] });
  const [deletedPicturesLocal, setDeletedPicturesLocal] = useState([]);
  const [savingPhotos, setSavingPhotos] = useState(false);
  // Variables de configuración de compresión de imágenes
  const maxWidth = parseInt(process.env.REACT_APP_IMAGE_MAX_WIDTH);
  const quality = parseFloat(process.env.REACT_APP_IMAGE_COMPRESSION_QUALITY);
  
  // Verificar si hay detalles con moneda USD
  const hasUSDDetails = useMemo(() => {
    return orderDetails.some(detail => detail.currency !== 1 && detail.currency !== "1");
  }, [orderDetails]);

  // Verificar si la cotización es inválida (vacía, 0 o menor a 0)
  const isInvalidCurrencyRate = useMemo(() => {
    if (!hasUSDDetails) return false;
    const rate = parseFloat(order?.currencyRate);
    return !order?.currencyRate || order.currencyRate === "" || isNaN(rate) || rate <= 0;
  }, [hasUSDDetails, order?.currencyRate]);
  
  const dateObject = new Date(order?.createdAt);
  // Get hours, minutes, and seconds
  const hours = dateObject.getHours();
  const minutes = dateObject.getMinutes();
  const seconds = dateObject.getSeconds();

  // Sincronizar fotos locales al abrir el modal
  const handleOpenEditPhotos = () => {
    setFormStateLocal({ pictures: order.files || [] });
    setDeletedPicturesLocal([]);
    setOpenEditPhotos(true);
  };

  // Guardar cambios en fotos
  const handleSavePhotos = async () => {
    setSavingPhotos(true);
    try {
      const orderId = order.orderId;
      const bucketId = order.bucketId || 'orders';
      // 1. Eliminar fotos marcadas
      if (deletedPicturesLocal.length > 0) {
        // Limpiar parámetros de URL si existen en los nombres de archivo y decodificar
        const cleanDeletedPictures = deletedPicturesLocal.map(name => decodeURIComponent(name.split('?')[0]));
        const deletedPaths = cleanDeletedPictures.map((name) => `${orderId}/${name}`);
        const { data: deleteData, error } = await supabase.storage.from(bucketId).remove(deletedPaths);
        if (error) {
          throw new Error(`Error al eliminar imágenes: ${error.message}`);
        }
      }
      // 2. Subir nuevas fotos
      const addedPictures = formStateLocal.pictures.filter(
        (item) => typeof item === 'object' && item?.isPictureAddedOnEdition && item.picture
      );
      if (addedPictures.length > 0) {
        for (const fileObj of addedPictures) {
          const file = fileObj.picture;
          let fileNamePart = file?.name
            ? file.name.toLowerCase().replace(/\s/g, "_")
            : `image_${Date.now()}.jpg`;
          const filename = `${orderId}/${uuidv4()}-${fileNamePart}`;
          
          // Comprimir la imagen antes de subir
          const compressedFile = await imageCompression(file, {
            maxSizeMB: 3,
            maxWidthOrHeight: maxWidth,
            initialQuality: quality,
            useWebWorker: false,
          });
          
          await supabase.storage.from(bucketId).upload(filename, compressedFile, {
            cacheControl: "3600",
            upsert: false,
          });
        }
      }
      // 3. Refrescar la lista de fotos desde el bucket
      const { data } = await supabase.storage.from(bucketId).list(`${orderId}/`);
      const files = await Promise.all(
        data.map(async (file) => {
          const { data } = supabase.storage
            .from(bucketId)
            .getPublicUrl(`${orderId}/${file.name}`);
          return data.publicUrl;
        })
      );
      setOrder({ ...order, files: files.filter(url => typeof url === 'string') });
      
      // Cerrar el modal con transición suave después de un pequeño delay
      setTimeout(() => {
        setIsClosingModal(true);
        setTimeout(() => {
          setOpenEditPhotos(false);
          setIsClosingModal(false);
        }, 300); // Duración de la transición de fade out
      }, 500);
    } catch (e) {
      alert('Error al guardar cambios de fotos: ' + e.message);
    } finally {
      setSavingPhotos(false);
    }
  };

  const fechaHora = `${order?.date || "—"} · ${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;

  const acciones = [
    { label: "Ver firma", icon: IconPen, onClick: () => { setPhotosUrlBakground([order?.clientSignaturePhoto]); setOpenPhotoModal(true); } },
    { label: "Ver fotos", icon: IconEye, onClick: () => { setPhotosUrlBakground(order?.files); setOpenPhotoModal(true); } },
    { label: "Editar fotos", icon: IconCamera, onClick: handleOpenEditPhotos },
  ];

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <FormCard
        title={`Orden N° ${order?.orderId ?? "—"}`}
        subtitle={fechaHora}
        action={
          order.status === orderObjectStatus.facturada
            ? <OrderStatusChip status={Number(order.status)} />
            : null
        }
        sx={{ width: "100%" }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(auto-fit, minmax(170px, 1fr))" },
            gap: { xs: "12px", md: "16px" },
            paddingBottom: "14px",
            borderBottom: `1px solid ${tokens.border}`,
          }}
        >
          <Dato label="Cliente">{order?.client?.company || "—"}</Dato>
          <Dato label="Firmado por">
            {order?.client?.name ? `${order?.client?.name} ${order?.client?.lastName}` : "ND"}
          </Dato>
          <Dato label="Fecha de cancelación" numeric>{order?.cancelledAt || "ND"}</Dato>
          <Dato label="Factura N°" numeric>—</Dato>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
            gap: { xs: "14px", md: "16px" },
          }}
        >
          {order.status !== orderObjectStatus.facturada && (
            <Field label="Estado">
              <SelectInput
                disabled={disableEdition}
                value={order.status}
                onChange={(e) => setOrder({ ...order, status: e.target.value })}
              >
                {mainOrderStatus.map((item, index) => (
                  <option key={index} value={item.value}>{item.label}</option>
                ))}
              </SelectInput>
            </Field>
          )}

          <Field
            label="Cotización"
            error={isInvalidCurrencyRate ? "Requerida: hay detalles en moneda extranjera" : undefined}
          >
            <TextInput
              type="text"
              inputMode="decimal"
              numeric
              invalid={isInvalidCurrencyRate}
              disabled={disableEdition}
              value={order.currencyRate || ""}
              placeholder="0,00"
              onChange={(e) => {
                let value = e.target.value.replace(/[^0-9.]/g, "");
                const parts = value.split(".");
                if (parts.length > 2) {
                  value = parts[0] + "." + parts.slice(1).join("");
                }
                setOrder({
                  ...order,
                  currencyRate: value,
                });
                if (disableEdition || !setOrderDetails) return;
                const rate = parseFloat(value);
                if (!isNaN(rate) && rate > 1) {
                  const usd = currencyValues[1].value;
                  const hadNonUsd = orderDetails.some(
                    (d) =>
                      d.currency !== usd && String(d.currency) !== String(usd)
                  );
                  setOrderDetails((prev) =>
                    prev.map((d) => ({ ...d, currency: usd }))
                  );
                  if (hadNonUsd) {
                    ErrorModal({
                      title: "Información",
                      text: "La moneda cambió a DOLARES (USD)",
                      icon: "info",
                    });
                  }
                }
              }}
            />
          </Field>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {acciones.map(({ label, icon: Icon, onClick }) => (
            <Button
              key={label}
              variant="outlined"
              startIcon={<Icon size={15} />}
              onClick={onClick}
              sx={{ minHeight: 40, flexGrow: { xs: 1, sm: 0 } }}
            >
              {label}
            </Button>
          ))}
        </Box>
      </FormCard>

      <PicturesModal
        open={openPhotoModal}
        setOpen={setOpenPhotoModal}
        images={photosUrlBakground}
      />
      <Modal 
        open={openEditPhotos} 
        onClose={() => setOpenEditPhotos(false)}
        sx={{
          '& .MuiBackdrop-root': {
            opacity: isClosingModal ? 0 : 0.5,
            transition: 'opacity 0.3s ease-in-out',
          }
        }}
      >
        <Box style={{
          position: 'relative',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          padding: 24,
          borderRadius: 8,
          minWidth: 600,
          width: '80vw',
          maxWidth: 1000,
          height: '90vh',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 90,
          opacity: isClosingModal ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out',
        }}>
          <Photos
            formState={formStateLocal}
            setFormState={setFormStateLocal}
            isOrderClosed={disableEdition}
            setDeletedPictures={setDeletedPicturesLocal}
            orderStatus={order.status}
            isEdition={true}
            hideClientInfo={true}
          />
          <Box
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 24,
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0 24px',
              background: 'white',
            }}
          >
            <button
              onClick={() => setOpenEditPhotos(false)}
              style={{
                background: '#434343',
                color: 'white',
                border: 'none',
                padding: '10px 24px',
                borderRadius: 4,
                fontSize: 16,
                minWidth: 140,
                cursor: 'pointer',
              }}
              disabled={savingPhotos}
            >
              Cancelar
            </button>
            <button
              style={{
                background: '#b91411',
                color: 'white',
                border: 'none',
                padding: '10px 24px',
                borderRadius: 4,
                fontSize: 16,
                minWidth: 140,
                cursor: 'pointer',
              }}
              onClick={handleSavePhotos}
              disabled={savingPhotos}
            >
              {savingPhotos ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </Box>
        </Box>
      </Modal>
      {savingPhotos && <FullScreenLoader message="Guardando los cambios de las fotos..." />}
    </Box>
  );
};

export default HeadOrder;
